import React, { useCallback, useEffect, useState } from "react";
import axios from "../utils/api.js";
import { useNavigate } from "react-router-dom";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "CAD", "AUD", "SGD", "JPY"];
const PLAN_STATUS = ["draft", "active", "deprecated"];
const PLAN_CATEGORY = ["main", "addon"];

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  fontSize: "14px",
  boxSizing: "border-box",
};

const emptyCreateForm = () => ({
  planName: "",
  description: "",
  planType: "",
  planCategory: "main",
  basePrice: "",
  currency: "INR",
  discountPercent: 0,
  sessionsPerMonth: "",
  hoursPerSession: "",
  unlimitedAccess: false,
  featuresText: "",
  validityDays: "",
  allowsAddOns: false,
  isCancellable: true,
  isRefundable: false,
  autoPayEnabled: false,
  billingCycle: "monthly",
  paymentProviderName: "Razorpay",
  providerPlanId: "",
  metadataStatus: "active",
  hoursPerMonth: "",
  validityMonths: "",
  isAutoPayRequired: false,
  autoPayMonths: "",
  freeMonths: "",
});

const parseFeatures = (text) =>
  String(text || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const computeFinalPrice = (basePrice, discountPercent) => {
  const base = Number(basePrice) || 0;
  const percent = Number(discountPercent) || 0;
  return Math.round(base - (base * percent) / 100);
};

const toNumberIfDefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const Snackbar = ({ message, open, onClose, type = "error" }) => (
  <div
    style={{
      position: "fixed",
      bottom: open ? "24px" : "-100px",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: type === "success" ? "#22c55e" : "#ef4444",
      color: "white",
      padding: "12px 20px",
      borderRadius: "8px",
      zIndex: 3000,
      transition: "bottom 0.25s ease",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
      maxWidth: "90%",
    }}
  >
    <span>{message}</span>
    <button
      type="button"
      onClick={onClose}
      style={{
        border: "none",
        background: "transparent",
        color: "white",
        fontSize: "18px",
        cursor: "pointer",
      }}
      aria-label="Close notification"
    >
      &times;
    </button>
  </div>
);

const CreatePlanDialog = ({ open, onClose, onCreated, setSnackbar }) => {
  const [form, setForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyCreateForm());
  }, [open]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.planName.trim();
    const type = form.planType.trim();
    const basePrice = Number(form.basePrice);
    const sessionsPerMonth = Number(form.sessionsPerMonth);
    const hoursPerSession = Number(form.hoursPerSession);
    const finalPrice = computeFinalPrice(basePrice, form.discountPercent);

    if (!name || !type) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Plan name and plan type are required.",
      });
      return;
    }
    if (!basePrice || basePrice <= 0) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Base price must be greater than 0.",
      });
      return;
    }
    if (!sessionsPerMonth || !hoursPerSession) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Sessions per month and hours per session are required.",
      });
      return;
    }
    if (finalPrice <= 0) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Final price must be greater than 0 after discount.",
      });
      return;
    }

    const payload = {
      planName: name,
      description: form.description || undefined,
      planType: type,
      planCategory: form.planCategory,
      pricing: {
        basePrice,
        currency: form.currency || "INR",
        discount: {
          percent: Number(form.discountPercent) || 0,
          finalPrice,
        },
      },
      billing: {
        autoPay: {
          enabled: !!form.autoPayEnabled,
          billingCycle: form.billingCycle || "monthly",
        },
        paymentProvider: {
          name: form.paymentProviderName || "Razorpay",
          ...(form.providerPlanId.trim()
            ? { planId: form.providerPlanId.trim() }
            : {}),
        },
      },
      benefits: {
        sessionsPerMonth,
        hoursPerSession,
        unlimitedAccess: !!form.unlimitedAccess,
        features: parseFeatures(form.featuresText),
      },
      restrictions: {
        validityDays: toNumberIfDefined(form.validityDays),
        allowsAddOns: !!form.allowsAddOns,
        isCancellable: !!form.isCancellable,
        isRefundable: !!form.isRefundable,
      },
      metadata: {
        status: form.metadataStatus,
      },
      ...(toNumberIfDefined(form.hoursPerMonth) !== undefined && {
        hoursPerMonth: toNumberIfDefined(form.hoursPerMonth),
      }),
      ...(toNumberIfDefined(form.validityMonths) !== undefined && {
        validityMonths: toNumberIfDefined(form.validityMonths),
      }),
      ...(form.isAutoPayRequired && { isAutoPayRequired: true }),
      ...(toNumberIfDefined(form.autoPayMonths) !== undefined && {
        autoPayMonths: toNumberIfDefined(form.autoPayMonths),
      }),
      ...(toNumberIfDefined(form.freeMonths) !== undefined && {
        freeMonths: toNumberIfDefined(form.freeMonths),
      }),
    };

    setCreating(true);
    try {
      await axios.post("/createsubscription", payload);
      setSnackbar({
        open: true,
        type: "success",
        message: "Subscription plan created successfully.",
      });
      onCreated();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create subscription plan.",
      });
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "16px",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          width: "100%",
          maxWidth: "820px",
          maxHeight: "92vh",
          overflowY: "auto",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 18px",
            borderBottom: "1px solid #f1f5f9",
            position: "sticky",
            top: 0,
            background: "white",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>
            Create subscription plan
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "22px",
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            &times;
          </button>
        </div>

        <form
          onSubmit={submit}
          style={{ padding: "18px", display: "grid", gap: "12px" }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Plan name *</label>
              <input
                style={inputStyle}
                value={form.planName}
                onChange={(e) => update("planName", e.target.value)}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Plan type *</label>
              <input
                style={inputStyle}
                value={form.planType}
                onChange={(e) => update("planType", e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...inputStyle, minHeight: "74px", resize: "vertical", fontFamily: "inherit" }}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Base price *</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.basePrice}
                onChange={(e) => update("basePrice", e.target.value)}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Currency</label>
              <select
                style={inputStyle}
                value={form.currency}
                onChange={(e) => update("currency", e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Discount %</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.discountPercent}
                onChange={(e) => update("discountPercent", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Final (preview)</label>
              <input
                readOnly
                style={{ ...inputStyle, background: "#f8fafc" }}
                value={computeFinalPrice(form.basePrice, form.discountPercent)}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Sessions/month *</label>
              <input
                type="number"
                min="1"
                style={inputStyle}
                value={form.sessionsPerMonth}
                onChange={(e) => update("sessionsPerMonth", e.target.value)}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Hours/session *</label>
              <input
                type="number"
                min="1"
                style={inputStyle}
                value={form.hoursPerSession}
                onChange={(e) => update("hoursPerSession", e.target.value)}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select
                style={inputStyle}
                value={form.planCategory}
                onChange={(e) => update("planCategory", e.target.value)}
              >
                {PLAN_CATEGORY.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select
                style={inputStyle}
                value={form.metadataStatus}
                onChange={(e) => update("metadataStatus", e.target.value)}
              >
                {PLAN_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Features (comma separated)</label>
            <input
              style={inputStyle}
              value={form.featuresText}
              onChange={(e) => update("featuresText", e.target.value)}
              placeholder="recordings, priority support"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Validity days</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.validityDays}
                onChange={(e) => update("validityDays", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Hours/month</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.hoursPerMonth}
                onChange={(e) => update("hoursPerMonth", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Validity months</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.validityMonths}
                onChange={(e) => update("validityMonths", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Auto-pay months</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.autoPayMonths}
                onChange={(e) => update("autoPayMonths", e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Free months</label>
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={form.freeMonths}
                onChange={(e) => update("freeMonths", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Provider name</label>
              <input
                style={inputStyle}
                value={form.paymentProviderName}
                onChange={(e) => update("paymentProviderName", e.target.value)}
              />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={labelStyle}>Existing provider plan ID (optional)</label>
              <input
                style={inputStyle}
                value={form.providerPlanId}
                onChange={(e) => update("providerPlanId", e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <Check
              label="Unlimited access"
              checked={form.unlimitedAccess}
              onChange={(v) => update("unlimitedAccess", v)}
            />
            <Check
              label="Auto-pay enabled"
              checked={form.autoPayEnabled}
              onChange={(v) => update("autoPayEnabled", v)}
            />
            <Check
              label="Allows add-ons"
              checked={form.allowsAddOns}
              onChange={(v) => update("allowsAddOns", v)}
            />
            <Check
              label="Cancellable"
              checked={form.isCancellable}
              onChange={(v) => update("isCancellable", v)}
            />
            <Check
              label="Refundable"
              checked={form.isRefundable}
              onChange={(v) => update("isRefundable", v)}
            />
            <Check
              label="Auto-pay required"
              checked={form.isAutoPayRequired}
              onChange={(v) => update("isAutoPayRequired", v)}
            />
          </div>

          {form.autoPayEnabled && (
            <div style={{ maxWidth: "220px" }}>
              <label style={labelStyle}>Billing cycle</label>
              <select
                style={inputStyle}
                value={form.billingCycle}
                onChange={(e) => update("billingCycle", e.target.value)}
              >
                <option value="monthly">monthly</option>
              </select>
            </div>
          )}

          <div
            style={{
              marginTop: "8px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button type="button" onClick={onClose} style={secondaryBtn}>
              Cancel
            </button>
            <button type="submit" disabled={creating} style={primaryBtn}>
              {creating ? "Creating..." : "Create plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Check = ({ label, checked, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </label>
);

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#64748b",
  marginBottom: "4px",
  fontWeight: "600",
};

const primaryBtn = {
  padding: "9px 14px",
  border: "none",
  borderRadius: "8px",
  background: "#6366f1",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

const secondaryBtn = {
  padding: "9px 14px",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  background: "#f8fafc",
  fontWeight: "600",
  cursor: "pointer",
};

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listMessage, setListMessage] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "error" });
  const navigate = useNavigate();

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setListMessage("");
    try {
      const res = await axios.get("/getsubscription");
      const rows = res.data?.data;
      setPlans(Array.isArray(rows) ? rows : []);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error;
      if (error.response?.status === 404) {
        setPlans([]);
        setListMessage(msg || "No subscription plans found.");
      } else {
        setSnackbar({
          open: true,
          message: msg || "Failed to fetch subscription plans.",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const beginEdit = (plan) => {
    setEditingId(plan._id);
    setEditData({
      planName: plan.planName || "",
      description: plan.description || "",
      planType: plan.planType || "",
      planCategory: plan.planCategory || "main",
      basePrice: plan.pricing?.basePrice ?? "",
      currency: plan.pricing?.currency || "INR",
      discountPercent: plan.pricing?.discount?.percent ?? 0,
      sessionsPerMonth: plan.benefits?.sessionsPerMonth ?? "",
      hoursPerSession: plan.benefits?.hoursPerSession ?? "",
      unlimitedAccess: !!plan.benefits?.unlimitedAccess,
      featuresText: (plan.benefits?.features || []).join(", "),
      validityDays: plan.restrictions?.validityDays ?? "",
      allowsAddOns: !!plan.restrictions?.allowsAddOns,
      isCancellable: plan.restrictions?.isCancellable ?? true,
      isRefundable: !!plan.restrictions?.isRefundable,
      autoPayEnabled: !!plan.billing?.autoPay?.enabled,
      billingCycle: plan.billing?.autoPay?.billingCycle || "monthly",
      paymentProviderName: plan.billing?.paymentProvider?.name || "Razorpay",
      providerPlanId: plan.billing?.paymentProvider?.planId || "",
      metadataStatus: plan.metadata?.status || "active",
      hoursPerMonth: plan.hoursPerMonth ?? "",
      validityMonths: plan.validityMonths ?? "",
      isAutoPayRequired: !!plan.isAutoPayRequired,
      autoPayMonths: plan.autoPayMonths ?? "",
      freeMonths: plan.freeMonths ?? "",
    });
  };

  const saveEdit = async (id) => {
    const name = String(editData.planName || "").trim();
    const type = String(editData.planType || "").trim();
    const basePrice = Number(editData.basePrice);
    const finalPrice = computeFinalPrice(basePrice, editData.discountPercent);
    const sessions = Number(editData.sessionsPerMonth);
    const hours = Number(editData.hoursPerSession);

    if (!name || !type) {
      setSnackbar({ open: true, type: "error", message: "Plan name and type are required." });
      return;
    }
    if (!basePrice || basePrice <= 0 || finalPrice <= 0) {
      setSnackbar({ open: true, type: "error", message: "Price values are invalid." });
      return;
    }
    if (!sessions || !hours) {
      setSnackbar({ open: true, type: "error", message: "Benefit values are invalid." });
      return;
    }

    const payload = {
      planName: name,
      description: editData.description || "",
      planType: type,
      planCategory: editData.planCategory || "main",
      pricing: {
        basePrice,
        currency: editData.currency || "INR",
        discount: {
          percent: Number(editData.discountPercent) || 0,
          finalPrice,
        },
      },
      billing: {
        autoPay: {
          enabled: !!editData.autoPayEnabled,
          billingCycle: editData.billingCycle || "monthly",
        },
        paymentProvider: {
          name: editData.paymentProviderName || "Razorpay",
          planId: String(editData.providerPlanId || "").trim(),
        },
      },
      benefits: {
        sessionsPerMonth: sessions,
        hoursPerSession: hours,
        unlimitedAccess: !!editData.unlimitedAccess,
        features: parseFeatures(editData.featuresText),
      },
      restrictions: {
        validityDays: toNumberIfDefined(editData.validityDays),
        allowsAddOns: !!editData.allowsAddOns,
        isCancellable: !!editData.isCancellable,
        isRefundable: !!editData.isRefundable,
      },
      metadata: { status: editData.metadataStatus || "active" },
      ...(toNumberIfDefined(editData.hoursPerMonth) !== undefined && {
        hoursPerMonth: toNumberIfDefined(editData.hoursPerMonth),
      }),
      ...(toNumberIfDefined(editData.validityMonths) !== undefined && {
        validityMonths: toNumberIfDefined(editData.validityMonths),
      }),
      ...(editData.isAutoPayRequired !== undefined && {
        isAutoPayRequired: !!editData.isAutoPayRequired,
      }),
      ...(toNumberIfDefined(editData.autoPayMonths) !== undefined && {
        autoPayMonths: toNumberIfDefined(editData.autoPayMonths),
      }),
      ...(toNumberIfDefined(editData.freeMonths) !== undefined && {
        freeMonths: toNumberIfDefined(editData.freeMonths),
      }),
    };

    try {
      await axios.put(`/put/subscription/${id}`, payload);
      setSnackbar({ open: true, type: "success", message: "Plan updated successfully." });
      setEditingId(null);
      setEditData({});
      fetchPlans();
    } catch (error) {
      setSnackbar({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update subscription plan.",
      });
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this subscription plan?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`/delete/subscription/${id}`);
      setSnackbar({ open: true, type: "success", message: "Plan deleted successfully." });
      setPlans((prev) => prev.filter((p) => p._id !== id));
    } catch (error) {
      setSnackbar({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to delete plan.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const updateStatus = async (id, status) => {
    setStatusLoadingId(id);
    try {
      await axios.patch(`/update-subscriptionplan-status/${id}`, { status });
      setSnackbar({ open: true, type: "success", message: "Status updated successfully." });
      setPlans((prev) =>
        prev.map((p) =>
          p._id === id
            ? { ...p, metadata: { ...(p.metadata || {}), status } }
            : p
        )
      );
      if (editingId === id) {
        setEditData((prev) => ({ ...prev, metadataStatus: status }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update status.",
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "36px 20px", minHeight: "100vh" }}>
      <CreatePlanDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchPlans}
        setSnackbar={setSnackbar}
      />
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
        <button type="button" style={secondaryBtn} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <button type="button" style={primaryBtn} onClick={() => setCreateOpen(true)}>
          + Create subscription
        </button>
      </div>

      <header style={{ textAlign: "center", marginBottom: "28px" }}>
        <h1
          style={{
            margin: "0 0 8px",
            fontSize: "34px",
            fontWeight: "800",
            color: "#1e293b",
            background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Subscription Plans
        </h1>
        <p style={{ margin: 0, color: "#64748b" }}>
          Create, update, delete and manage subscription status.
        </p>
      </header>

      {loading && plans.length === 0 ? (
        <div style={{ textAlign: "center", color: "#64748b", padding: "36px" }}>Loading plans...</div>
      ) : plans.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "34px",
            color: "#64748b",
            background: "#f8fafc",
          }}
        >
          {listMessage || "No plans available."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: "18px" }}>
          {plans.map((plan) => {
            const status = plan.metadata?.status || "draft";
            const isEditing = editingId === plan._id;
            const finalPrice = plan.pricing?.discount?.finalPrice;
            const previewFinal = computeFinalPrice(editData.basePrice, editData.discountPercent);
            return (
              <div
                key={plan._id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  background: "white",
                  padding: "16px",
                  boxShadow: "0 5px 20px rgba(15,23,42,0.05)",
                }}
              >
                {!isEditing ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <h3 style={{ margin: "0 0 4px", color: "#1e293b" }}>{plan.planName}</h3>
                        <div style={{ color: "#64748b", fontSize: "14px" }}>{plan.description || "—"}</div>
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          padding: "4px 10px",
                          borderRadius: "999px",
                          background: status === "active" ? "#dcfce7" : status === "deprecated" ? "#fee2e2" : "#e2e8f0",
                          color: status === "active" ? "#166534" : status === "deprecated" ? "#991b1b" : "#475569",
                        }}
                      >
                        {status}
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop: "14px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: "1px solid #ddd6fe",
                        background: "linear-gradient(90deg, #eef2ff, #f5f3ff)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <div style={{ fontSize: "12px", color: "#6366f1", fontWeight: "700", textTransform: "uppercase" }}>
                        Price Flow
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: "15px", fontWeight: "700", color: "#475569", textDecoration: "line-through" }}>
                          {plan.pricing?.currency || "INR"} {plan.pricing?.basePrice ?? "-"}
                        </span>
                        <span style={{ fontSize: "18px", fontWeight: "700", color: "#6366f1" }}>
                          →
                        </span>
                        <span style={{ fontSize: "24px", fontWeight: "800", color: "#4f46e5" }}>
                          {plan.pricing?.currency || "INR"} {finalPrice ?? "-"}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(4, minmax(140px, 1fr))", gap: "10px" }}>
                      <Stat label="Type" value={plan.planType || "-"} />
                      <Stat label="Category" value={plan.planCategory || "-"} />
                      <Stat label="Base Price" value={`${plan.pricing?.currency || "INR"} ${plan.pricing?.basePrice ?? "-"}`} />
                      <Stat label="Discount %" value={plan.pricing?.discount?.percent ?? "-"} />
                      <Stat label="Sessions/Month" value={plan.benefits?.sessionsPerMonth ?? "-"} />
                      <Stat label="Hours/Session" value={plan.benefits?.hoursPerSession ?? "-"} />
                      <Stat label="Unlimited Access" value={plan.benefits?.unlimitedAccess ? "Yes" : "No"} />
                      <Stat label="AutoPay" value={plan.billing?.autoPay?.enabled ? "Yes" : "No"} />
                      <Stat label="Billing Cycle" value={plan.billing?.autoPay?.billingCycle || "-"} />
                      <Stat label="Provider" value={plan.billing?.paymentProvider?.name || "-"} />
                      <Stat label="Provider Plan ID" value={plan.billing?.paymentProvider?.planId || "-"} />
                      <Stat label="Validity Days" value={plan.restrictions?.validityDays ?? "-"} />
                      <Stat label="Allows AddOns" value={plan.restrictions?.allowsAddOns ? "Yes" : "No"} />
                      <Stat label="Cancellable" value={plan.restrictions?.isCancellable ? "Yes" : "No"} />
                      <Stat label="Refundable" value={plan.restrictions?.isRefundable ? "Yes" : "No"} />
                      <Stat label="Version" value={plan.metadata?.version ?? "-"} />
                      <Stat label="Hours/Month" value={plan.hoursPerMonth ?? "-"} />
                      <Stat label="Validity Months" value={plan.validityMonths ?? "-"} />
                      <Stat label="AutoPay Required" value={plan.isAutoPayRequired ? "Yes" : "No"} />
                      <Stat label="AutoPay Months" value={plan.autoPayMonths ?? "-"} />
                      <Stat label="Free Months" value={plan.freeMonths ?? "-"} />
                    </div>
                    <div style={{ marginTop: "10px" }}>
                      <Stat
                        label="Features"
                        value={
                          Array.isArray(plan.benefits?.features) && plan.benefits.features.length
                            ? plan.benefits.features.join(", ")
                            : "-"
                        }
                      />
                    </div>

                    <div style={{ marginTop: "12px" }}>
                      <label style={labelStyle}>Status</label>
                      <select
                        style={{ ...inputStyle, maxWidth: "220px" }}
                        value={status}
                        disabled={statusLoadingId === plan._id}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === status) return;
                          updateStatus(plan._id, next);
                        }}
                      >
                        {PLAN_STATUS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                      <button type="button" style={primaryBtn} onClick={() => beginEdit(plan)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePlan(plan._id)}
                        disabled={deletingId === plan._id}
                        style={{
                          ...primaryBtn,
                          background: deletingId === plan._id ? "#fca5a5" : "#ef4444",
                        }}
                      >
                        {deletingId === plan._id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#1e293b" }}>Edit subscription plan</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <div>
                        <label style={labelStyle}>Plan name</label>
                        <input style={inputStyle} value={editData.planName} onChange={(e) => setEditData((p) => ({ ...p, planName: e.target.value }))} />
                      </div>
                      <div>
                        <label style={labelStyle}>Plan type</label>
                        <input style={inputStyle} value={editData.planType} onChange={(e) => setEditData((p) => ({ ...p, planType: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ marginTop: "10px" }}>
                      <label style={labelStyle}>Description</label>
                      <textarea
                        style={{ ...inputStyle, minHeight: "72px", resize: "vertical", fontFamily: "inherit" }}
                        value={editData.description}
                        onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))}
                      />
                    </div>
                    <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                      <div>
                        <label style={labelStyle}>Base price</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.basePrice}
                          onChange={(e) => setEditData((p) => ({ ...p, basePrice: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Currency</label>
                        <select
                          style={inputStyle}
                          value={editData.currency}
                          onChange={(e) => setEditData((p) => ({ ...p, currency: e.target.value }))}
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Discount %</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.discountPercent}
                          onChange={(e) => setEditData((p) => ({ ...p, discountPercent: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Final (preview)</label>
                        <input readOnly style={{ ...inputStyle, background: "#f8fafc" }} value={previewFinal} />
                      </div>
                    </div>

                    <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                      <div>
                        <label style={labelStyle}>Sessions/month</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.sessionsPerMonth}
                          onChange={(e) => setEditData((p) => ({ ...p, sessionsPerMonth: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Hours/session</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.hoursPerSession}
                          onChange={(e) => setEditData((p) => ({ ...p, hoursPerSession: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Category</label>
                        <select
                          style={inputStyle}
                          value={editData.planCategory}
                          onChange={(e) => setEditData((p) => ({ ...p, planCategory: e.target.value }))}
                        >
                          {PLAN_CATEGORY.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Status</label>
                        <select
                          style={inputStyle}
                          value={editData.metadataStatus}
                          onChange={(e) => setEditData((p) => ({ ...p, metadataStatus: e.target.value }))}
                        >
                          {PLAN_STATUS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                      <div>
                        <label style={labelStyle}>Validity days</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.validityDays}
                          onChange={(e) => setEditData((p) => ({ ...p, validityDays: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Hours/month</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.hoursPerMonth}
                          onChange={(e) => setEditData((p) => ({ ...p, hoursPerMonth: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Validity months</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.validityMonths}
                          onChange={(e) => setEditData((p) => ({ ...p, validityMonths: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Auto-pay months</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.autoPayMonths}
                          onChange={(e) => setEditData((p) => ({ ...p, autoPayMonths: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                      <div>
                        <label style={labelStyle}>Free months</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={editData.freeMonths}
                          onChange={(e) => setEditData((p) => ({ ...p, freeMonths: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Provider name</label>
                        <input
                          style={inputStyle}
                          value={editData.paymentProviderName}
                          onChange={(e) => setEditData((p) => ({ ...p, paymentProviderName: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Provider plan ID</label>
                        <input
                          style={inputStyle}
                          value={editData.providerPlanId}
                          onChange={(e) => setEditData((p) => ({ ...p, providerPlanId: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Billing cycle</label>
                        <select
                          style={inputStyle}
                          value={editData.billingCycle}
                          onChange={(e) => setEditData((p) => ({ ...p, billingCycle: e.target.value }))}
                        >
                          <option value="monthly">monthly</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: "10px" }}>
                      <label style={labelStyle}>Features (comma separated)</label>
                      <input
                        style={inputStyle}
                        value={editData.featuresText}
                        onChange={(e) => setEditData((p) => ({ ...p, featuresText: e.target.value }))}
                      />
                    </div>

                    <div style={{ marginTop: "10px", display: "flex", gap: "14px", flexWrap: "wrap" }}>
                      <Check label="Unlimited access" checked={editData.unlimitedAccess} onChange={(v) => setEditData((p) => ({ ...p, unlimitedAccess: v }))} />
                      <Check label="Auto-pay enabled" checked={editData.autoPayEnabled} onChange={(v) => setEditData((p) => ({ ...p, autoPayEnabled: v }))} />
                      <Check label="Allows add-ons" checked={editData.allowsAddOns} onChange={(v) => setEditData((p) => ({ ...p, allowsAddOns: v }))} />
                      <Check label="Cancellable" checked={editData.isCancellable} onChange={(v) => setEditData((p) => ({ ...p, isCancellable: v }))} />
                      <Check label="Refundable" checked={editData.isRefundable} onChange={(v) => setEditData((p) => ({ ...p, isRefundable: v }))} />
                      <Check label="Auto-pay required" checked={editData.isAutoPayRequired} onChange={(v) => setEditData((p) => ({ ...p, isAutoPayRequired: v }))} />
                    </div>

                    <div style={{ marginTop: "12px", display: "flex", gap: "10px" }}>
                      <button type="button" style={primaryBtn} onClick={() => saveEdit(plan._id)}>
                        Save
                      </button>
                      <button
                        type="button"
                        style={secondaryBtn}
                        onClick={() => {
                          setEditingId(null);
                          setEditData({});
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "2px" }}>{label}</div>
    <div style={{ color: "#1e293b", fontWeight: "600", wordBreak: "break-word" }}>{value}</div>
  </div>
);

export default SubscriptionPlansPage;
